const INVISIBLE_FORMAT_CHARS = /[\u200B-\u200D\uFEFF]/g;
const PERSON_NAME_ALLOWED = /^[\p{L}\p{M}\s.'-]+$/u;

function stripInvisibleFormatChars(value) {
  return String(value || '').replace(INVISIBLE_FORMAT_CHARS, '').trim();
}

function isValidPersonDisplayName(name) {
  const text = stripInvisibleFormatChars(name);
  if (!text) return false;
  if (!/[\p{L}]/u.test(text)) return false;
  return PERSON_NAME_ALLOWED.test(text);
}

function emailLocalPartLabel(email) {
  const emailText = String(email || '').trim();
  if (!emailText.includes('@')) return '';
  const local = emailText.split('@')[0].replace(/[._+-]+/g, ' ').trim();
  return local;
}

export function initialsFromName(name = '') {
  const tokens = stripInvisibleFormatChars(name).split(/\s+/).filter(Boolean);
  const initials = [];
  for (const token of tokens) {
    const letters = [...token].filter((ch) => /\p{L}/u.test(ch));
    if (letters.length === 0) continue;
    initials.push(letters[0].toUpperCase());
    if (initials.length >= 2) break;
  }
  return initials.join('');
}

/** Safe display label for person names; falls back to email local-part for legacy bad rows. */
export function displayPersonName(name, emailFallback = '') {
  const text = stripInvisibleFormatChars(name);
  if (isValidPersonDisplayName(text)) return text;
  const fromEmail = emailLocalPartLabel(emailFallback);
  if (fromEmail) return fromEmail;
  return 'Unknown user';
}

export function classNames(...args) {
  return args.filter(Boolean).join(' ');
}

export function fmtPct(n) {
  if (n === undefined || n === null) return '—';
  return `${Math.round(n)}%`;
}

export function fmtMMSS(seconds) {
  if (seconds == null) return '00:00';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

// All HireIQ timestamps are stored as naive UTC on the backend. The UI must
// always render them in India Standard Time regardless of the viewer's machine
// timezone.
export const IST_TIME_ZONE = 'Asia/Kolkata';

// Parse a backend datetime value into a Date. A string without an explicit
// timezone designator (no trailing `Z` and no `±HH:MM` offset) is interpreted
// as UTC — never the viewer's local time — because that is how the backend
// stores it. Returns null for empty/invalid input.
export function parseServerDate(value) {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  let s = String(value).trim();
  const hasZone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(s);
  if (!hasZone) {
    s = `${s.replace(' ', 'T')}Z`;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

// Format a backend datetime as date + time in IST. Defaults to a medium date
// and short time (e.g. "29 Jun 2026, 4:30 pm").
export function fmtDateTimeIST(value, options = { dateStyle: 'medium', timeStyle: 'short' }, fallback = '—') {
  const d = parseServerDate(value);
  if (!d) return fallback;
  return d.toLocaleString('en-IN', { timeZone: IST_TIME_ZONE, ...options });
}

// Format a backend datetime as a date only in IST.
export function fmtDateIST(value, options = { day: '2-digit', month: 'short', year: 'numeric' }, fallback = '—') {
  const d = parseServerDate(value);
  if (!d) return fallback;
  return d.toLocaleDateString('en-IN', { timeZone: IST_TIME_ZONE, ...options });
}

// Format a backend datetime as a time only in IST.
export function fmtTimeIST(value, options = { hour: 'numeric', minute: '2-digit' }, fallback = '—') {
  const d = parseServerDate(value);
  if (!d) return fallback;
  return d.toLocaleTimeString('en-IN', { timeZone: IST_TIME_ZONE, ...options });
}

/** Truncate text to a maximum number of words, appending an ellipsis when clipped. */
export function truncateToWords(text, maxWords = 20) {
  if (!text) return '';
  const words = String(text).trim().split(/\s+/).filter(Boolean);
  if (words.length <= maxWords) return words.join(' ');
  return `${words.slice(0, maxWords).join(' ')}…`;
}

/**
 * Normalize a panel overall score to the 0–5 scale used in client reports.
 * CandidateAssessment stores 0–100; legacy PanelEvaluation payloads use 0–10.
 */
export function panelScoreToOn5(score) {
  if (score == null || Number.isNaN(Number(score))) return null;
  const n = Number(score);
  if (n <= 10) return Math.round((n / 2) * 10) / 10;
  return Math.round((n / 20) * 10) / 10;
}
